import { type RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { type LayoutChangeEvent, type NativeMethods, type ScrollView } from 'react-native';
import {
  type AnimatedRef,
  runOnJS,
  useAnimatedReaction,
  useAnimatedRef,
  useScrollViewOffset,
} from 'react-native-reanimated';
import type Animated from 'react-native-reanimated';

import { useActorRef, useSelector } from '@xstate5/react';

import { ScrollSpyMachineTypes } from './machine';
import { scrollspyMachine } from './machine';

// ─────────────────────────────────────────────────────────────────────────────

export const useScrollspy = (params?: UseScrollspyMachineParams) => {
  const { defaultTargetId, onActiveTargetIdChange } = params ?? {};

  // ─── Refs ──────────────────────────────────────────────────────────────

  const scrollViewRef = useAnimatedRef<Animated.ScrollView>();
  const scrollOffset = useScrollViewOffset(scrollViewRef);
  const navScrollViewRef = useRef<ScrollView>(null);

  // ─── External Actions ──────────────────────────────────────────────────

  const onActiveTargetChange = useCallback<ScrollSpyMachineTypes.ScrollspyAction>(
    ({ context }) => {
      if (!context.activeTargetId) return;

      onActiveTargetIdChange?.(context.activeTargetId);
    },
    [onActiveTargetIdChange],
  );

  // ─── Machine ───────────────────────────────────────────────────────────

  const machine = useMemo(
    () =>
      scrollspyMachine.provide({
        actions: { onActiveTargetChange },
      }),
    [onActiveTargetChange],
  );

  const actorRef = useActorRef(machine, {
    input: { defaultTargetId, scrollOffset, scrollViewRef, navScrollViewRef },
  });

  const activeTargetId = useSelector(actorRef, (snapshot) => snapshot.context.activeTargetId);

  const pendingDefaultTargetId = useSelector(
    actorRef,
    (snapshot) => snapshot.context.defaultTargetId,
  );

  const hasDefaultMeasurement = useSelector(actorRef, (snapshot) =>
    pendingDefaultTargetId ? Boolean(snapshot.context.measurements[pendingDefaultTargetId]) : false,
  );

  // ─── Helpers ───────────────────────────────────────────────────────────

  const scrollToDefaultTarget = useCallback(() => {
    if (!pendingDefaultTargetId || !hasDefaultMeasurement) return;

    actorRef.send({ type: 'defaultTarget.scrollTo', targetId: pendingDefaultTargetId });
  }, [pendingDefaultTargetId, hasDefaultMeasurement, actorRef]);

  // ─── Scroll Tracking ───────────────────────────────────────────────────

  const sendScroll = useCallback(() => {
    actorRef.send({ type: 'scroll' });
  }, [actorRef]);

  // ─── Measurement ───────────────────────────────────────────────────────

  const measureTargetFactory = useCallback(
    (targetId: string, targetRef: RefObject<unknown>) => {
      return (_x: number, y: number, _width: number, height: number) => {
        actorRef.send({ type: 'target.measure', targetId, targetRef, measurement: { y, height } });
      };
    },
    [actorRef],
  );

  const measureCoordinates = useCallback(
    (targetId: string, targetRef: RefObject<unknown>) => {
      const measureTarget = measureTargetFactory(targetId, targetRef);

      requestAnimationFrame(() => {
        const targetNode = (targetRef.current as NativeMethods | null) ?? null;
        const scrollViewNode = (scrollViewRef.current as NativeMethods | null) ?? null;

        if (!scrollViewNode || !targetNode) return;

        targetNode?.measureLayout?.(scrollViewNode, measureTarget);
      });
    },
    [measureTargetFactory, scrollViewRef],
  );

  const deregisterTarget = useCallback(
    (targetId: string) => {
      actorRef.send({ type: 'target.deregister', targetId });
    },
    [actorRef],
  );

  // ─── Event Forwarders ──────────────────────────────────────────────────

  const scrollTo = useCallback(
    (targetId: string) => {
      actorRef.send({ type: 'target.scrollTo', targetId });
    },
    [actorRef],
  );

  const onScrollViewContentSizeChange = useCallback(
    (_width: number, height: number) => {
      actorRef.send({ type: 'scrollView.contentSizeChange', height });
    },
    [actorRef],
  );

  const onScrollViewLayout = useCallback(
    (event: LayoutChangeEvent) => {
      actorRef.send({ type: 'scrollView.layout', height: event.nativeEvent.layout.height });
    },
    [actorRef],
  );

  // ─── Effects ───────────────────────────────────────────────────────────

  useEffect(scrollToDefaultTarget, [scrollToDefaultTarget]);

  // NOTE: Callbacks must be inline — Reanimated babel plugin converts them to worklets.
  useAnimatedReaction(
    () => scrollOffset.value,
    () => {
      runOnJS(sendScroll)();
    },
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    activeTargetId,
    scrollViewRef,
    navScrollViewRef,
    scrollOffset,
    scrollTo,
    measureCoordinates,
    deregisterTarget,
    onScrollViewContentSizeChange,
    onScrollViewLayout,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseScrollspyMachineParams = {
  defaultTargetId?: string;
  onActiveTargetIdChange?: (targetId: string) => void;
};

export type ScrollspyScrollViewRef = AnimatedRef<Animated.ScrollView>;
